import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useClientUsage } from '../../hooks/useClientData';

const COLORS = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#0891b2'];

export function UsagePage() {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const getDateRange = () => {
    const end = new Date();
    let start: Date;
    
    switch (dateRange) {
      case '7d':
        start = subDays(end, 7);
        break;
      case '90d':
        start = subDays(end, 90);
        break;
      case '30d':
      default:
        start = subDays(end, 30);
    }
    
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  };

  const { startDate, endDate } = getDateRange();

  const { data: usage, isLoading } = useClientUsage(startDate, endDate);

  const handleExport = () => {
    if (!usage) return;
    
    const csv = [
      ['Date', 'Labels Generated'].join(','),
      ...usage.byDay.map(d => [d.date, d.count].join(',')),
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-${startDate}-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const pieData = usage?.byTemplate.map(t => ({
    name: t.templateKey,
    value: t.count,
  })) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Usage</h1>
        <div className="flex items-center space-x-3">
          <div className="flex rounded-lg overflow-hidden border">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                className={`px-3 py-1.5 text-sm ${
                  dateRange === range
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setDateRange(range)}
              >
                {range}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={handleExport}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Total Labels</div>
          <div className="text-3xl font-bold text-gray-900">
            {usage?.totalLabels.toLocaleString() ?? 0}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Successful</div>
          <div className="text-3xl font-bold text-green-600">
            {usage?.successfulLabels.toLocaleString() ?? 0}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Failed</div>
          <div className="text-3xl font-bold text-red-600">
            {usage?.failedLabels.toLocaleString() ?? 0}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Avg Latency</div>
          <div className="text-3xl font-bold text-gray-900">
            {usage?.avgLatencyMs ?? 0}ms
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Usage Chart */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Usage</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usage?.byDay ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => format(new Date(value), 'MM/dd')}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                />
                <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Template Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">By Template</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => 
                    `${name.substring(0, 10)}${name.length > 10 ? '...' : ''} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Detailed Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4">Template</th>
                <th className="text-left py-2 px-4">Retailer</th>
                <th className="text-left py-2 px-4">Label Type</th>
                <th className="text-right py-2 px-4">Count</th>
                <th className="text-right py-2 px-4">Avg Latency</th>
                <th className="text-right py-2 px-4">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {usage?.byTemplate.map((item) => (
                <tr key={item.templateKey} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium">{item.templateKey}</td>
                  <td className="py-2 px-4">{item.retailer}</td>
                  <td className="py-2 px-4">
                    <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                      {item.labelType}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right">{item.count.toLocaleString()}</td>
                  <td className="py-2 px-4 text-right">{item.avgLatencyMs}ms</td>
                  <td className="py-2 px-4 text-right">
                    {usage?.totalLabels 
                      ? ((item.count / usage.totalLabels) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
